/**
 * fissalCleanup.js — Text normalisation for web AI chat display.
 *
 * Preserves:
 *   - Markdown tables (| col | col |)
 *   - Image tags (![alt](url)) and chart URLs (/charts/...)
 *   - Markdown links [text](url) and valid formatting
 *   - Fenced code blocks
 *
 * Cleans:
 *   - Collapses 3+ consecutive blank lines → single blank
 *   - Strips Discord RP action lines (-# …)
 *   - Fixes leaked formatting inside inline code backticks
 *   - Normalizes non-standard bullet points to standard markdown `- `
 *   - Linkifies bare UESP wiki citations [Online:Title] → [Title](<https://en.uesp.net/wiki/...>)
 *   - Unwraps double-bracketed links and strips stray numeric citation markers [0]
 */

const WIKI_NAMESPACES = 'Online|Lore|General|Skyrim|Oblivion|Morrowind|Daggerfall|Arena|Blades|Legends';
const UESP_BASE = 'https://en.uesp.net/wiki/';
const BARE_CITATION = new RegExp(`\\[((?:${WIKI_NAMESPACES}):[^\\]\\n]{1,100})\\](?!\\()`, 'g');
const LINKED_WITH_NAMESPACE = new RegExp(`\\[(?:${WIKI_NAMESPACES}):([^\\]\\n]{1,100})\\](?=\\()`, 'g');
const WRAPPED_LINK = /\[(\[[^\]\n]{1,100}\]\(<https?:\/\/[^>\s]+>\))(?:,[^\]\n]{0,40})?\]/g;
const NUMERIC_CITATION = /[ \t]?\[\d{1,2}(?:\s*,\s*\d{1,2})*\](?!\()/g;

function uespUrlForTitle(title) {
  const slug = encodeURI(String(title).trim().replace(/ /g, '_'))
    .replace(/\(/g, '%28')
    .replace(/\)/g, '%29');
  return `${UESP_BASE}${slug}`;
}

function linkifySegment(segment) {
  let out = segment.replace(BARE_CITATION, (_m, title) => {
    const label = title.replace(/^[A-Za-z]+:/, '').trim();
    return `[${label}](<${uespUrlForTitle(title)}>)`;
  });
  out = out.replace(LINKED_WITH_NAMESPACE, '[$1]');
  out = out.replace(WRAPPED_LINK, '$1');
  out = out.replace(NUMERIC_CITATION, '');
  return out;
}

function linkifyWikiCitations(line) {
  if (!line || typeof line !== 'string' || line.indexOf('[') === -1) return line;
  return line
    .split(/(`[^`\n]*`)/)
    .map((seg, i) => (i % 2 === 1 ? seg : linkifySegment(seg)))
    .join('');
}

/**
 * Clean Fissal output for web display while preserving rich web markdown, tables, images, and links.
 * @param {string} text
 * @returns {string}
 */
export function cleanFissalOutput(text) {
  if (!text || typeof text !== 'string') return text;

  const lines = text.split('\n');
  const normalized = [];
  let inCodeBlock = false;

  for (let line of lines) {
    // Preserve code blocks verbatim (don't mangle code content)
    if (/^```/.test(line.trimStart())) {
      inCodeBlock = !inCodeBlock;
      normalized.push(line);
      continue;
    }
    if (inCodeBlock) {
      normalized.push(line);
      continue;
    }

    line = line.trimEnd();

    // Collapse consecutive blank lines → at most one
    if (!line) {
      if (normalized.length > 0 && normalized[normalized.length - 1] !== '') {
        normalized.push('');
      }
      continue;
    }

    // Strip Discord RP action lines (-# …) — web UI doesn't render them
    if (/^-#\s/.test(line)) {
      continue;
    }

    // Linkify bare wiki citations and unwrap double brackets
    line = linkifyWikiCitations(line);

    // Fix inline code: strip markdown formatting that leaked inside backticks
    line = line.replace(/`([^`\n]+?)`/g, (_m, inner) => {
      return '`' + inner.replace(/(\*\*\*|\*\*|\*|__|_|~~)/g, '') + '`';
    });

    // Normalize non-standard bullet markers to - (unless inside a table row)
    if (!line.trim().startsWith('|')) {
      line = line.replace(/^(\s*)[*+•○◦▪▫]\s/, '$1- ');
    }

    // Drop heading lines that degenerated to bare '#' markers
    if (/^#+\s*$/.test(line.trim())) continue;

    if (line.trim()) normalized.push(line);
  }

  // Remove trailing blank lines
  while (normalized.length && normalized[normalized.length - 1] === '') {
    normalized.pop();
  }

  return normalized.join('\n');
}
